import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import html2pdf from "html2pdf.js";
import apiService from "../../Services/apiServices";
import helper from "../../Services/helper";
import { useToasts } from "react-toast-notifications";
import {
  buildPreparedItems,
  findBestItemMatch,
  findHeaderKey,
  ITEM_NAME_HEADERS,
  normalizeText,
} from "./comparisonUtils";

const getMatchBadgeClass = (type) => {
  if (type === "Exact") return "badge badge-success";
  if (type === "Approx") return "badge badge-warning";
  return "badge badge-danger";
};

const getMatchLabel = (type) => {
  if (type === "Exact") return "I sakte";
  if (type === "Approx") return "I perafert";
  return "Nuk u gjet";
};

const getMatchPdfRowStyle = (type) => {
  if (type === "Exact") {
    return { background: "#dcfce7", color: "#166534" };
  }

  if (type === "Approx") {
    return { background: "#fef3c7", color: "#92400e" };
  }

  return { background: "#fee2e2", color: "#991b1b" };
};

const MATCH_STATUS_FILTERS = [
  { value: "All", label: "Te gjitha" },
  { value: "Exact", label: "I sakte" },
  { value: "Approx", label: "I perafert" },
  { value: "Not Found", label: "Nuk u gjet" },
];

const formatScore = (score) => `${Math.round((score || 0) * 100)}%`;

const getExportRows = (rows) =>
  rows.map((result) => ({
    Rreshti: result.rowNumber,
    ItemName: result.excelItemName,
    "Artikulli nga API": result.matchedItemName || "",
    Statusi: getMatchLabel(result.matchType),
    "Ngjashmeria (%)": Math.round((result.matchScore || 0) * 100),
    "Product No": result.matchedProductNo || "",
    Barcode: result.matchedBarcode || "",
    "Item ID": result.matchedItemId || "",
  }));

const compareExcelRows = (rows, itemNameKey, preparedLookup) => {
  return rows
    .map((row, index) => {
      const excelItemName = String(row?.[itemNameKey] || "").trim();

      if (!normalizeText(excelItemName)) {
        return null;
      }

      const { matchedItem, matchType, matchScore } = findBestItemMatch(
        { itemName: excelItemName },
        preparedLookup
      );

      return {
        rowNumber: index + 2,
        excelItemName,
        matchedItemId: matchedItem?.id || null,
        matchedItemName: matchedItem?.itemName || "",
        matchedProductNo: matchedItem?.productNo || "",
        matchedBarcode: matchedItem?.barcode || "",
        matchType,
        matchScore,
      };
    })
    .filter(Boolean);
};

function Comparisons() {
  const { addToast } = useToasts();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [processingFile, setProcessingFile] = useState(false);
  const [results, setResults] = useState([]);
  const [fileName, setFileName] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => {
    let isMounted = true;

    apiService()
      .get("items")
      .then((response) => {
        if (!isMounted) return;

        if (response.success) {
          setItems(response.data || []);
        } else {
          addToast(
            response.errorMessage || "Ndodhi nje gabim gjate marrjes se artikujve.",
            { appearance: "error", autoDismiss: true }
          );
        }
      })
      .catch(() => {
        if (!isMounted) return;

        addToast("Nuk u arrit te merren artikujt nga API.", {
          appearance: "error",
          autoDismiss: true,
        });
      })
      .finally(() => {
        if (isMounted) {
          setLoadingItems(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [addToast]);

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (loadingItems) {
      addToast("Prit derisa te merren artikujt nga API.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    if (!items.length) {
      addToast("Lista e artikujve eshte bosh ose nuk u ngarkua nga API.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    setProcessingFile(true);
    setFileName(file.name);

    const reader = new FileReader();

    reader.onload = (loadEvent) => {
      try {
        const workbook = XLSX.read(loadEvent.target.result, {
          type: "binary",
          cellDates: true,
        });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          defval: "",
        });

        if (!rows.length) {
          setResults([]);
          addToast("Excel file nuk ka rreshta per krahasim.", {
            appearance: "warning",
            autoDismiss: true,
          });
          return;
        }

        const itemNameKey = findHeaderKey(rows[0], ITEM_NAME_HEADERS);

        if (!itemNameKey) {
          setResults([]);
          addToast("Excel file duhet ta kete kolonen itemName.", {
            appearance: "error",
            autoDismiss: true,
          });
          return;
        }

        const preparedLookup = buildPreparedItems(items);
        const comparedRows = compareExcelRows(rows, itemNameKey, preparedLookup);

        setStatusFilter("All");
        setResults(comparedRows);

        addToast(
          `U krahasuan ${comparedRows.length} rreshta nga ${file.name}.`,
          { appearance: "success", autoDismiss: true }
        );
      } catch (error) {
        setResults([]);
        addToast("Leximi i Excel file deshtoi.", {
          appearance: "error",
          autoDismiss: true,
        });
      } finally {
        setProcessingFile(false);
      }
    };

    reader.onerror = () => {
      setProcessingFile(false);
      setResults([]);
      addToast("Ndodhi nje gabim gjate hapjes se file-it.", {
        appearance: "error",
        autoDismiss: true,
      });
    };

    reader.readAsBinaryString(file);
  };

  let exactMatches = 0;
  let approximateMatches = 0;
  let notFoundMatches = 0;

  results.forEach((result) => {
    if (result.matchType === "Exact") exactMatches++;
    else if (result.matchType === "Approx") approximateMatches++;
    else notFoundMatches++;
  });

  const normalizedSearch = normalizeText(search);
  const filteredResults = results.filter((result) => {
    if (statusFilter !== "All" && result.matchType !== statusFilter) {
      return false;
    }

    if (!normalizedSearch) return true;

    return [
      result.excelItemName,
      result.matchedItemName,
      result.matchedProductNo,
      result.matchedBarcode,
      result.matchType,
      getMatchLabel(result.matchType),
    ].some((value) => normalizeText(value).includes(normalizedSearch));
  });

  const exportToExcel = () => {
    if (!filteredResults.length) {
      addToast("Nuk ka rezultate per export.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(getExportRows(filteredResults));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Krahasimi");
    XLSX.writeFile(workbook, "krahasimi-artikujve.xlsx");
  };

  const createPdfCell = (row, value, isHeader = false, customStyle = {}) => {
    const cell = document.createElement(isHeader ? "th" : "td");
    cell.textContent = value;
    cell.style.border = "1px solid #d1d5db";
    cell.style.padding = "6px";
    cell.style.fontSize = "9px";
    cell.style.textAlign = isHeader ? "left" : "inherit";

    if (isHeader) {
      cell.style.background = "#f3f4f6";
      cell.style.fontWeight = "700";
    }

    Object.keys(customStyle).forEach((key) => {
      cell.style[key] = customStyle[key];
    });

    row.appendChild(cell);
  };

  const exportToPdf = () => {
    if (!filteredResults.length) {
      addToast("Nuk ka rezultate per export.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    const wrapper = document.createElement("div");
    wrapper.style.padding = "18px";
    wrapper.style.background = "#ffffff";
    wrapper.style.color = "#111827";
    wrapper.style.fontFamily = "Arial, sans-serif";

    const title = document.createElement("h3");
    title.textContent = "Krahasimi i Artikujve";
    title.style.margin = "0 0 8px";
    wrapper.appendChild(title);

    const meta = document.createElement("div");
    meta.textContent = `File: ${fileName || "-"} | Filter: ${statusFilter === "All" ? "Te gjitha" : getMatchLabel(statusFilter)} | Rreshta: ${filteredResults.length}`;
    meta.style.marginBottom = "12px";
    meta.style.fontSize = "10px";
    wrapper.appendChild(meta);

    const table = document.createElement("table");
    table.style.width = "100%";
    table.style.borderCollapse = "collapse";

    const headerRow = document.createElement("tr");
    [
      "Rreshti",
      "ItemName",
      "Artikulli nga API",
      "Statusi",
      "Ngjashmeria",
      "Product No",
      "Barcode",
      "Item ID",
    ].forEach((header) => createPdfCell(headerRow, header, true));
    table.appendChild(headerRow);

    filteredResults.forEach((result) => {
      const row = document.createElement("tr");
      const rowStyle = getMatchPdfRowStyle(result.matchType);
      const statusStyle = { ...rowStyle, fontWeight: "700" };

      createPdfCell(row, String(result.rowNumber), false, rowStyle);
      createPdfCell(row, result.excelItemName, false, rowStyle);
      createPdfCell(row, result.matchedItemName || "", false, rowStyle);
      createPdfCell(
        row,
        getMatchLabel(result.matchType),
        false,
        statusStyle
      );
      createPdfCell(row, formatScore(result.matchScore), false, rowStyle);
      createPdfCell(row, result.matchedProductNo || "", false, rowStyle);
      createPdfCell(row, result.matchedBarcode || "", false, rowStyle);
      createPdfCell(row, String(result.matchedItemId || ""), false, rowStyle);
      table.appendChild(row);
    });

    wrapper.appendChild(table);

    html2pdf()
      .set({
        margin: 8,
        filename: "krahasimi-artikujve.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      })
      .from(wrapper)
      .save();
  };

  return (
    <div className="container-fluid px-2 mt-3">
      <div className="card shadow">
        <div className="card-header py-2 d-flex flex-wrap align-items-center justify-content-between">
          <h6 className="m-0 font-weight-bold text-primary">
            Krahasimi i Artikujve
          </h6>
          <span className="small text-muted">
            Artikuj nga API: {items.length}
          </span>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-lg-4 mb-3">
              <label className="font-weight-bold mb-1">Excel file</label>
              <input
                type="file"
                className="form-control"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={loadingItems || processingFile}
              />
              <small className="form-text text-muted">
                Kolona e detyrueshme: <strong>itemName</strong>
              </small>
              {fileName ? (
                <small className="d-block text-muted mt-2">
                  File i ngarkuar: {fileName}
                </small>
              ) : null}
            </div>

            <div className="col-lg-3 mb-3">
              <label className="font-weight-bold mb-1">
                Kerko ne rezultate
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="itemName, productNo, barcode..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                disabled={!results.length}
              />
              <small className="form-text text-muted">
                Exact match bazohet ne emrin e pastruar. Approx match perdor
                ngjashmeri te larte tekstuale.
              </small>
            </div>

            <div className="col-lg-2 mb-3">
              <label className="font-weight-bold mb-1">Filtro statusin</label>
              <select
                className="form-control"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                disabled={!results.length}
              >
                {MATCH_STATUS_FILTERS.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </select>
              <small className="form-text text-muted">
                Filtron edhe export-in.
              </small>
            </div>

            <div className="col-lg-3 mb-3">
              <label className="font-weight-bold mb-1">Statusi</label>
              <div className="border rounded p-3 h-100 bg-light">
                {loadingItems && (
                  <div className="small text-muted">Duke i marre artikujt...</div>
                )}
                {!loadingItems && processingFile && (
                  <div className="small text-muted">Duke e lexuar Excel file...</div>
                )}
                {!loadingItems && !processingFile && (
                  <div className="small text-muted">
                    {results.length
                      ? `Gati per shqyrtim. Rreshta te krahasuar: ${results.length}`
                      : "Ngarko nje Excel file per krahasim."}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
            <div className="small text-muted mb-2 mb-md-0">
              Rezultate te shfaqura: {filteredResults.length} / {results.length}
            </div>
            <div>
              <button
                type="button"
                className="btn btn-success btn-sm mr-2"
                onClick={exportToExcel}
                disabled={!filteredResults.length}
              >
                <i className="fas fa-file-excel mr-1"></i>
                Export Excel
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={exportToPdf}
                disabled={!filteredResults.length}
              >
                <i className="fas fa-file-pdf mr-1"></i>
                Export PDF
              </button>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-3 mb-2">
              <div className="border rounded p-3 h-100">
                <div className="text-xs text-uppercase text-muted mb-1">Total</div>
                <div className="h5 mb-0">{results.length}</div>
              </div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="border rounded p-3 h-100">
                <div className="text-xs text-uppercase text-muted mb-1">Exact</div>
                <div className="h5 mb-0 text-success">{exactMatches}</div>
              </div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="border rounded p-3 h-100">
                <div className="text-xs text-uppercase text-muted mb-1">Approx</div>
                <div className="h5 mb-0 text-warning">{approximateMatches}</div>
              </div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="border rounded p-3 h-100">
                <div className="text-xs text-uppercase text-muted mb-1">Not Found</div>
                <div className="h5 mb-0 text-danger">{notFoundMatches}</div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-hover mb-0">
              <thead className="thead-light">
                <tr>
                  <th style={{ width: "70px" }}>Rreshti</th>
                  <th>{helper().translate("ITEMNAME")}</th>
                  <th>Artikulli nga API</th>
                  <th style={{ width: "120px" }}>Statusi</th>
                  <th style={{ width: "110px" }}>Ngjashmeria</th>
                  <th style={{ width: "150px" }}>Product No</th>
                  <th style={{ width: "160px" }}>Barcode</th>
                </tr>
              </thead>
              <tbody>
                {!filteredResults.length ? (
                  <tr>
                    <td colSpan="7" className="text-center p-4">
                      {helper().translate("NO_DATA")}
                    </td>
                  </tr>
                ) : (
                  filteredResults.map((result, index) => (
                    <tr key={`${result.rowNumber}-${result.excelItemName}-${index}`}>
                      <td>{result.rowNumber}</td>
                      <td>{result.excelItemName}</td>
                      <td>
                        {result.matchedItemId ? (
                          <a href={`#/item/${result.matchedItemId}`}>
                            {result.matchedItemName}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <span className={getMatchBadgeClass(result.matchType)}>
                          {getMatchLabel(result.matchType)}
                        </span>
                      </td>
                      <td>{formatScore(result.matchScore)}</td>
                      <td>{result.matchedProductNo || "-"}</td>
                      <td>{result.matchedBarcode || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Comparisons;
